import type { Hook } from './actions';
import type { Modifier } from './stats';

/**
 * Account companions. Bounded power: level 1..10, each level adds 5% to the pet's
 * ability and owner bonuses. Variety over strength; pets never fight in duels.
 */
export interface PetDef {
  id: string;
  /** follower unit in MOBS */
  unit: string;
  color: string;
  accent: string;
  glyph: string;
  /** modifiers applied to the owner, scaled by level */
  mods: Modifier[];
  /** hooks on the owner (caster = owner), scaled by level through hook power */
  hooks?: Hook[];
  /** fame price; 0 = owned by default */
  cost: number;
}

export const PET_MAX_LEVEL = 10;

export function petXpForLevel(level: number): number {
  return 25 + level * level * 8;
}

export function petLevelMul(level: number): number {
  return 1 + 0.05 * (Math.max(1, Math.min(PET_MAX_LEVEL, level)) - 1);
}

export const PETS: Record<string, PetDef> = {
  wolf: { id: 'wolf', unit: 'pet_wolf', color: '#8a7a6a', accent: '#e0c8a0', glyph: '🐺', mods: [{ stat: 'damage', inc: 0.02 }], cost: 0 },
  raven: { id: 'raven', unit: 'pet_raven', color: '#2a2a3a', accent: '#8a8aff', glyph: '🐦', mods: [{ stat: 'pickupRadius', inc: 0.6 }, { stat: 'xpGain', inc: 0.06 }, { stat: 'luck', add: 1 }], cost: 120 },
  wisp: { id: 'wisp', unit: 'pet_wisp', color: '#ffe9a8', accent: '#ffffff', glyph: '✨', mods: [{ stat: 'healPower', inc: 0.08 }], cost: 150 },
  tortoise: { id: 'tortoise', unit: 'pet_tortoise', color: '#4f6f4a', accent: '#c8e0b0', glyph: '🐢', mods: [{ stat: 'armor', add: 5 }], hooks: [{ on: 'damaged', cooldown: 10, action: { type: 'buff', target: 'self', status: [{ id: 'shield', duration: 5, power: 0.12 }], visual: 'ward' } }], cost: 150 },
  bat: { id: 'bat', unit: 'pet_bat', color: '#4a3a5c', accent: '#c8a0e0', glyph: '🦇', mods: [{ stat: 'lifesteal', add: 0.02 }], hooks: [{ on: 'kill', chance: 0.1, cooldown: 1, action: { type: 'heal', target: 'self', pct: 0.03, visual: 'heal' } }], cost: 180 },
  cat: { id: 'cat', unit: 'pet_cat', color: '#3d2a5a', accent: '#b48cff', glyph: '🐈', mods: [{ stat: 'cooldown', inc: -0.03 }], cost: 200 },
};

export const PET_IDS = Object.keys(PETS);

export interface PetProgress {
  level: number;
  xp: number;
}

export interface PetsState {
  active: string | null;
  owned: Record<string, PetProgress>;
}

export function defaultPets(): PetsState {
  return { active: 'wolf', owned: { wolf: { level: 1, xp: 0 } } };
}

/** Adds xp to a pet and levels it up to the cap. Returns levels gained. */
export function addPetXp(p: PetProgress, xp: number): number {
  let gained = 0;
  p.xp += Math.max(0, Math.round(xp));
  while (p.level < PET_MAX_LEVEL && p.xp >= petXpForLevel(p.level)) {
    p.xp -= petXpForLevel(p.level);
    p.level++;
    gained++;
  }
  if (p.level >= PET_MAX_LEVEL) p.xp = Math.min(p.xp, petXpForLevel(PET_MAX_LEVEL));
  return gained;
}
